import type { InventoryRowEdit, StagedRowBaseline } from 'src/admin/models/PoolInventoryModel';

/**
 * Reducer-backed slice of one pool's inventory state: the staged-change
 * structures whose transitions carry invariants (edit merging, the
 * baseline's exists-iff-referenced lockstep), plus the submit and CSV
 * lifecycles. Simple per-instance values (page, search text) live here
 * too so one dispatch path drives every transition.
 */
export interface PoolInventoryState {
    /** Raw text in the symbol-search box; typing stays responsive. */
    symbolSearchQuery: string;
    /**
     * Committed symbol-search term sent to the backend as the `symbol`
     * query parameter. Trails {@link symbolSearchQuery} by a debounce so
     * live typing fires a single backend search instead of one request
     * per keystroke. Empty means "no filter — page the full pool".
     */
    appliedSymbolSearch: string;
    addSymbolDraft: string;
    rowEdits: Record<string, InventoryRowEdit>;
    pendingRowRemovals: ReadonlyArray<string>;
    /**
     * Row snapshots captured when an edit / removal was staged, keyed
     * by row id. The submit-review dialog renders from these instead
     * of a full-pool re-fetch (PLAT-12889). Kept in lockstep with
     * {@link rowEdits} + {@link pendingRowRemovals}: an entry drops out
     * when the last staged change on the row is cleared.
     */
    stagedRowBaselines: Record<string, StagedRowBaseline>;
    submitReviewOpen: boolean;
    /** True while a submit is in flight. */
    isSubmitting: boolean;
    /** Last submit-failure message, or null. */
    submitError: string | null;
    /** True while the pool CSV export is in flight. */
    isDownloadingCsv: boolean;
    /** 1-based current page of the inventory table (raw; reads clamp). */
    inventoryPage: number;
}

export const initialPoolInventoryState: PoolInventoryState = {
    symbolSearchQuery: '',
    appliedSymbolSearch: '',
    addSymbolDraft: '',
    rowEdits: {},
    pendingRowRemovals: [],
    stagedRowBaselines: {},
    submitReviewOpen: false,
    isSubmitting: false,
    submitError: null,
    isDownloadingCsv: false,
    inventoryPage: 1,
};

export type PoolInventoryAction =
    | { type: 'SET_SYMBOL_SEARCH_QUERY'; query: string }
    | { type: 'SET_APPLIED_SYMBOL_SEARCH'; query: string }
    | { type: 'SET_INVENTORY_PAGE'; page: number }
    | { type: 'SET_ADD_SYMBOL_DRAFT'; value: string }
    | { type: 'STAGE_ROW_EDIT'; edit: InventoryRowEdit; baseline?: StagedRowBaseline }
    | { type: 'CLEAR_ROW_FIELD_EDIT'; rowId: string; field: 'currentQuantity' | 'currentLabPrice' }
    | { type: 'STAGE_ROW_REMOVAL'; rowId: string; baseline?: StagedRowBaseline }
    | { type: 'UNSTAGE_ROW_REMOVAL'; rowId: string }
    | { type: 'RESET_EDITS' }
    | { type: 'CLEAR_STAGED_CHANGES'; rowEditIds: ReadonlyArray<string>; removedRowIds: ReadonlyArray<string> }
    | { type: 'OPEN_SUBMIT_REVIEW' }
    | { type: 'CLOSE_SUBMIT_REVIEW' }
    | { type: 'START_SUBMIT' }
    | { type: 'SUCCEED_SUBMIT' }
    | { type: 'FAIL_SUBMIT'; error: string }
    | { type: 'CLEAR_SUBMIT_ERROR' }
    | { type: 'START_CSV_DOWNLOAD' }
    | { type: 'END_CSV_DOWNLOAD' };

export const setSymbolSearchQuery = (query: string): PoolInventoryAction => ({
    type: 'SET_SYMBOL_SEARCH_QUERY',
    query,
});
export const setAppliedSymbolSearch = (query: string): PoolInventoryAction => ({
    type: 'SET_APPLIED_SYMBOL_SEARCH',
    query,
});
export const setInventoryPage = (page: number): PoolInventoryAction => ({ type: 'SET_INVENTORY_PAGE', page });
export const setAddSymbolDraft = (value: string): PoolInventoryAction => ({ type: 'SET_ADD_SYMBOL_DRAFT', value });
/**
 * Stage (or merge into) a row's field edits. `baseline` carries the
 * row's current server values so the submit-review preview can render
 * before-values without a full-pool fetch (PLAT-12889).
 */
export const stageRowEdit = (edit: InventoryRowEdit, baseline?: StagedRowBaseline): PoolInventoryAction => ({
    type: 'STAGE_ROW_EDIT',
    edit,
    baseline,
});
export const clearRowFieldEdit = (
    rowId: string,
    field: 'currentQuantity' | 'currentLabPrice',
): PoolInventoryAction => ({ type: 'CLEAR_ROW_FIELD_EDIT', rowId, field });
export const stageRowRemoval = (rowId: string, baseline?: StagedRowBaseline): PoolInventoryAction => ({
    type: 'STAGE_ROW_REMOVAL',
    rowId,
    baseline,
});
export const unstageRowRemoval = (rowId: string): PoolInventoryAction => ({ type: 'UNSTAGE_ROW_REMOVAL', rowId });
export const resetEdits = (): PoolInventoryAction => ({ type: 'RESET_EDITS' });
/**
 * Drop only the listed staged edits / removals (a partial submit's
 * successes), pruning baselines that no staged change references.
 */
export const clearStagedChanges = (
    rowEditIds: ReadonlyArray<string>,
    removedRowIds: ReadonlyArray<string>,
): PoolInventoryAction => ({ type: 'CLEAR_STAGED_CHANGES', rowEditIds, removedRowIds });
export const openSubmitReview = (): PoolInventoryAction => ({ type: 'OPEN_SUBMIT_REVIEW' });
export const closeSubmitReview = (): PoolInventoryAction => ({ type: 'CLOSE_SUBMIT_REVIEW' });
export const startSubmit = (): PoolInventoryAction => ({ type: 'START_SUBMIT' });
export const succeedSubmit = (): PoolInventoryAction => ({ type: 'SUCCEED_SUBMIT' });
export const failSubmit = (error: string): PoolInventoryAction => ({ type: 'FAIL_SUBMIT', error });
export const clearSubmitError = (): PoolInventoryAction => ({ type: 'CLEAR_SUBMIT_ERROR' });
export const startCsvDownload = (): PoolInventoryAction => ({ type: 'START_CSV_DOWNLOAD' });
export const endCsvDownload = (): PoolInventoryAction => ({ type: 'END_CSV_DOWNLOAD' });

function mergeEdit(prev: InventoryRowEdit | undefined, next: InventoryRowEdit): InventoryRowEdit {
    if (!prev) return next;
    return { ...prev, ...next };
}

function editsEqual(a: InventoryRowEdit, b: InventoryRowEdit): boolean {
    return a.rowId === b.rowId && a.currentQuantity === b.currentQuantity && a.currentLabPrice === b.currentLabPrice;
}

function omitBaseline(baselines: Record<string, StagedRowBaseline>, rowId: string): Record<string, StagedRowBaseline> {
    if (baselines[rowId] === undefined) return baselines;
    const { [rowId]: _dropped, ...rest } = baselines;
    return rest;
}

export function poolInventoryReducer(state: PoolInventoryState, action: PoolInventoryAction): PoolInventoryState {
    switch (action.type) {
        case 'SET_SYMBOL_SEARCH_QUERY':
            if (state.symbolSearchQuery === action.query) return state;
            // Narrowing the filter can leave the current page out of range;
            // snap back to page 1 so the user always sees results.
            return { ...state, symbolSearchQuery: action.query, inventoryPage: 1 };

        case 'SET_APPLIED_SYMBOL_SEARCH':
            if (state.appliedSymbolSearch === action.query) return state;
            // The committed term drives the backend query; reset the page
            // too so the (now different) result set opens at the top.
            return { ...state, appliedSymbolSearch: action.query, inventoryPage: 1 };

        case 'SET_INVENTORY_PAGE':
            if (state.inventoryPage === action.page) return state;
            return { ...state, inventoryPage: action.page };

        case 'SET_ADD_SYMBOL_DRAFT':
            if (state.addSymbolDraft === action.value) return state;
            return { ...state, addSymbolDraft: action.value };

        case 'STAGE_ROW_EDIT': {
            const next = mergeEdit(state.rowEdits[action.edit.rowId], action.edit);
            const prev = state.rowEdits[action.edit.rowId];
            if (prev && editsEqual(prev, next)) return state;
            return {
                ...state,
                rowEdits: { ...state.rowEdits, [action.edit.rowId]: next },
                // Overwrite any prior baseline whenever the staged edit
                // actually changes (identical re-stages short-circuit above):
                // the action carries the row's current server values, so the
                // preview's before-values track the freshest server state —
                // matching the old snapshot-at-open behavior.
                stagedRowBaselines: action.baseline
                    ? { ...state.stagedRowBaselines, [action.edit.rowId]: action.baseline }
                    : state.stagedRowBaselines,
            };
        }

        case 'CLEAR_ROW_FIELD_EDIT': {
            const existing = state.rowEdits[action.rowId];
            if (!existing || existing[action.field] === undefined) return state;
            const nextEdit: InventoryRowEdit = { rowId: existing.rowId };
            if (action.field !== 'currentQuantity' && existing.currentQuantity !== undefined) {
                nextEdit.currentQuantity = existing.currentQuantity;
            }
            if (action.field !== 'currentLabPrice' && existing.currentLabPrice !== undefined) {
                nextEdit.currentLabPrice = existing.currentLabPrice;
            }
            const isEmpty = nextEdit.currentQuantity === undefined && nextEdit.currentLabPrice === undefined;
            const { [action.rowId]: _removed, ...remainingEdits } = state.rowEdits;
            // The baseline lives as long as any staged change references the
            // row; a still-pending removal keeps it alive.
            const dropBaseline = isEmpty && !state.pendingRowRemovals.includes(action.rowId);
            return {
                ...state,
                rowEdits: isEmpty ? remainingEdits : { ...remainingEdits, [action.rowId]: nextEdit },
                stagedRowBaselines: dropBaseline
                    ? omitBaseline(state.stagedRowBaselines, action.rowId)
                    : state.stagedRowBaselines,
            };
        }

        case 'STAGE_ROW_REMOVAL':
            if (state.pendingRowRemovals.includes(action.rowId)) return state;
            return {
                ...state,
                pendingRowRemovals: [...state.pendingRowRemovals, action.rowId],
                stagedRowBaselines: action.baseline
                    ? { ...state.stagedRowBaselines, [action.rowId]: action.baseline }
                    : state.stagedRowBaselines,
            };

        case 'UNSTAGE_ROW_REMOVAL': {
            if (!state.pendingRowRemovals.includes(action.rowId)) return state;
            // Keep the baseline while staged edits still reference the row.
            const dropBaseline = state.rowEdits[action.rowId] === undefined;
            return {
                ...state,
                pendingRowRemovals: state.pendingRowRemovals.filter((id) => id !== action.rowId),
                stagedRowBaselines: dropBaseline
                    ? omitBaseline(state.stagedRowBaselines, action.rowId)
                    : state.stagedRowBaselines,
            };
        }

        case 'RESET_EDITS':
            if (
                Object.keys(state.rowEdits).length === 0 &&
                state.pendingRowRemovals.length === 0 &&
                state.addSymbolDraft === '' &&
                !state.submitReviewOpen &&
                state.submitError === null &&
                !state.isSubmitting
            ) {
                return state;
            }
            // Close the submit-review dialog and clear any failed-submit
            // error / in-flight submitting state: keeping them around would
            // leave a stale dialog, a dead-end error banner, or a submit bar
            // stuck disabled on `isSubmitting === true` after a user reset.
            return {
                ...state,
                rowEdits: {},
                pendingRowRemovals: [],
                stagedRowBaselines: {},
                addSymbolDraft: '',
                submitReviewOpen: false,
                submitError: null,
                isSubmitting: false,
            };

        case 'CLEAR_STAGED_CHANGES': {
            const editIds = new Set(action.rowEditIds);
            const removedIds = new Set(action.removedRowIds);
            if (editIds.size === 0 && removedIds.size === 0) return state;

            // Only allocate new collections when something actually matches —
            // a non-empty id list whose entries aren't staged shouldn't break
            // the reducer's reference-identity short-circuit.
            let nextEdits: Record<string, InventoryRowEdit> = state.rowEdits;
            let editsChanged = false;
            if (editIds.size > 0) {
                const rebuilt: Record<string, InventoryRowEdit> = {};
                for (const [rowId, edit] of Object.entries(state.rowEdits)) {
                    if (editIds.has(rowId)) editsChanged = true;
                    else rebuilt[rowId] = edit;
                }
                if (editsChanged) nextEdits = rebuilt;
            }

            let nextRemovals: ReadonlyArray<string> = state.pendingRowRemovals;
            let removalsChanged = false;
            if (removedIds.size > 0) {
                const filtered = state.pendingRowRemovals.filter((id) => !removedIds.has(id));
                if (filtered.length !== state.pendingRowRemovals.length) {
                    nextRemovals = filtered;
                    removalsChanged = true;
                }
            }

            if (!editsChanged && !removalsChanged) return state;

            // Prune baselines for rows no longer referenced by either
            // structure, preserving the "baseline exists iff some staged
            // change references the row" invariant.
            let nextBaselines: Record<string, StagedRowBaseline> = state.stagedRowBaselines;
            const removalsLeft = new Set(nextRemovals);
            const orphaned = Object.keys(state.stagedRowBaselines).filter(
                (rowId) => nextEdits[rowId] === undefined && !removalsLeft.has(rowId),
            );
            if (orphaned.length > 0) {
                nextBaselines = { ...state.stagedRowBaselines };
                for (const rowId of orphaned) delete nextBaselines[rowId];
            }

            return {
                ...state,
                rowEdits: nextEdits,
                pendingRowRemovals: nextRemovals,
                stagedRowBaselines: nextBaselines,
            };
        }

        case 'OPEN_SUBMIT_REVIEW':
            if (state.submitReviewOpen) return state;
            return { ...state, submitReviewOpen: true };

        case 'CLOSE_SUBMIT_REVIEW':
            if (!state.submitReviewOpen) return state;
            return { ...state, submitReviewOpen: false };

        case 'START_SUBMIT':
            if (state.isSubmitting && state.submitError === null) return state;
            return { ...state, isSubmitting: true, submitError: null };

        case 'SUCCEED_SUBMIT':
            if (!state.isSubmitting && state.submitError === null) return state;
            return { ...state, isSubmitting: false, submitError: null };

        case 'FAIL_SUBMIT':
            if (!state.isSubmitting && state.submitError === action.error) return state;
            return { ...state, isSubmitting: false, submitError: action.error };

        case 'CLEAR_SUBMIT_ERROR':
            if (state.submitError === null) return state;
            return { ...state, submitError: null };

        case 'START_CSV_DOWNLOAD':
            if (state.isDownloadingCsv) return state;
            return { ...state, isDownloadingCsv: true };

        case 'END_CSV_DOWNLOAD':
            if (!state.isDownloadingCsv) return state;
            return { ...state, isDownloadingCsv: false };

        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
